<template>
  <div>
    <OpeningHoursTable :rows="table" />
    <template v-if="exceptions.length">
      <h3 class="title is-h5 mt-size-2 mb-size-6">
        {{ $texts('openingHoursExceptions') }}
      </h3>
      <OpeningHoursTable :rows="exceptions" />
    </template>
  </div>
</template>

<script lang="ts" setup>
import type { WeekdayTableRow, WeekdaySlot } from './types'
import { getLocaleForLanguage } from '~/helpers'

const props = withDefaults(
  defineProps<{
    days?: Array<{
      day: number
      start: number
      end: number
      comment?: string
    }>
  }>(),
  {
    days: () => [],
  },
)

const { $texts } = useEasyTexts()
const language = useCurrentLanguage()
const locale = computed(() => getLocaleForLanguage(language.value))

const weekdays = computed(() => [
  $texts('sunday'),
  $texts('monday'),
  $texts('tuesday'),
  $texts('wednesday'),
  $texts('thursday'),
  $texts('friday'),
  $texts('saturday'),
])

const table = computed(() => {
  const weekdaysSorted = [...weekdays.value.slice(1), weekdays.value[0]]
  const weekdaysMap = weekdaysSorted.reduce<Record<string, WeekdayTableRow>>(
    (acc, v, index) => {
      if (v) {
        acc[v] = {
          day: index,
          name: v,
          slots: [],
        }
      }
      return acc
    },
    {},
  )
  const days = props.days || []
  const map = days.reduce((acc, day) => {
    const weekday = weekdays.value[day.day]
    if (weekday && acc[weekday]) {
      acc[weekday].slots.push(mapDay(day))
    }
    return acc
  }, weekdaysMap)
  return Object.values(map).sort((a, b) =>
    (a.day ?? 0) < (b.day ?? 0) ? -1 : 1,
  )
})

const exceptions = computed(() => {
  const items = [...props.days]
    .filter((v) => v.day > 6)
    .sort((a, b) => a.day - b.day)
    .reduce(
      (acc, v) => {
        const date = new Date(v.day * 1000)
        const key = date.toISOString()
        if (!acc[key]) {
          acc[key] = {
            name: date.toLocaleDateString(locale.value, {
              month: 'long',
              day: 'numeric',
              year: 'numeric',
            }),
            date,
            slots: [],
          }
        }
        const day = mapDay(v)
        if (day.start !== day.end && day.start !== '00:00') {
          acc[key].slots.push(day)
        }
        return acc
      },
      {} as Record<string, { name: string; date: Date; slots: WeekdaySlot[] }>,
    )
  return Object.values(items)
})

function formatTime(time: number) {
  if (!Number.isInteger(time)) {
    return ''
  }
  const str = time.toString().padStart(4, '0')
  return [str.slice(0, 2), str.slice(2)].join(':')
}

function mapDay(day: { start: number; end: number }): WeekdaySlot {
  const start = formatTime(day.start)
  let end = formatTime(day.end)
  if (end === '00:00') {
    end = '24:00'
  }
  return {
    start,
    end,
  }
}
</script>
